import XLSX from "xlsx";
import { toastrService } from '@/helpers/services/notification/toastrService';
import TemplateTalentModel from '@/state/models/templateTalentModel';
import { restService } from '@/helpers/services/restService';
import Swal from 'sweetalert2';
import i18n from '@/i18n';
import router from '@/router';
import TalentModel from '@/state/models/talentModel';
import { multiselectHelper } from "@/helpers/utils/multiselectHelper";
import QualificationModel from "@/state/models/qualificationModel";
import { authHeader } from "../auth/authHeader";


const bulkCreationUrl: string = restService.buildURL(process.env.VUE_APP_API_TALENT_CREATE_BULK);

const qualificationsUrl: string = restService.buildURL(process.env.VUE_APP_API_MANDATORY_QUALIFICATIONS_URL);


/**
 * Send given array of user-objects to API
 * and return amount of success/error creations
 * 
 * Usually, you either use name/params or path/query combination
 * This hack allows us to re-render the component
 * 
 * @param talents[] Array of talents to be uploaded
 * @param departmentId UUID of department/pool
 * @param pools Array of objects with result from MultiSelect in component (consists of 'id' and 'title')
 *
 * @link https://www.sinocalife.com/vue-router-same-page-redundant-route-navigation-and-usage-of-router-push
 */
async function _uploadTalents(talents: TemplateTalentModel[], departmentId: string, pools: Object[], qualifications: QualificationModel[]) {
  const mappedTalents: TalentModel[] = _mapHeadersToInternalFormat(talents, qualifications);

  let poolIds: String[]|null = null;
  if (pools) {
    poolIds = multiselectHelper.trimEverythingButIds(pools);
  }

  const data: any = {
    departmentId,
    talents: mappedTalents,
  };

  if (pools) {
    data.poolIds = poolIds;
  }

  const bulkUploadResult = await restService.post(bulkCreationUrl, data, authHeader(), "");

  // success
  if (bulkUploadResult === "") {
    return router.push({
      name: "bulk-import",
      path: "/talents/bulk-import",
      params: {
        importType: toastrService.SUCCESS_TYPE,
      },
      query: {
        importType: toastrService.SUCCESS_TYPE,
      }
    });
  }

  // @ts-ignore
  return router.push({
    name: "bulk-import",
    path: "/talents/bulk-import",
    params: {
      importMessage: bulkUploadResult,
      importType: toastrService.ERROR_TYPE,
    },
    query: {
      importMessage: bulkUploadResult,
      importType: toastrService.ERROR_TYPE,
    }
  }).catch(() => {});
}

/**
 * Convert given excel file to
 * array of its rows (as objects) and
 * call method that double-checks and uploads the file
 * 
 * @param selectedFile 
 * @param destination UUID of department/pool
 */
function addMultipleTalentsByFile(selectedFile: File, departmentId: string, pools: Object[]) {
  if (!selectedFile) {
    toastrService.showError(i18n.t("talents.create.bulk.no-file-error"));
    return;
  }

  const reader = new FileReader();
  reader.onload = e => {
    // @ts-ignore
    const data = e.target.result;
    const workbook = XLSX.read(data, {
      type: "binary",
    });

    // As this shit is asynchronous, returning the users
    // is pretty fucked up and we therefore nest this like this
    const users: TemplateTalentModel[] = XLSX.utils.sheet_to_json(workbook.Sheets[workbook.SheetNames[0]]);
    _addMultipleTalents(users, departmentId, pools);
  };

  reader.onerror = e => {
    // @ts-ignore
    console.error("Could not read file: " + e.target.error.code);
    toastrService.showError(i18n.t("talents.create.bulk.reading-error"));
  }
  reader.readAsBinaryString(selectedFile);
}

/**
 * Let the user confirm the bulk upload
 * and call method that takes care of the upload
 * to the given destination
 * 
 * @param talents[] Array of talents to be uploaded 
 * @param destination UUID of department/pool
 */
function _addMultipleTalents(talents: TemplateTalentModel[], departmentId: string, pools: Object[]) {
  if (!talents.length) {
    toastrService.showError(i18n.t("talents.create.bulk.no-talents-found-error"));
    return false;
  }

  if (!departmentId) {
    toastrService.showError(i18n.t("talents.create.bulk.no-destination-found-error"));
    return false;
  }

  Swal.fire({
    title: i18n.t("talents.create.bulk.confirm-modal.title"),
    html: i18n.t(
      "talents.create.bulk.confirm-modal.text",
      {
        talentsAmount: talents.length,
      }
    ),
    icon: 'warning',
    confirmButtonText: `<i class="fa fa-upload" aria-hidden="true"></i> ${i18n.t("talents.create.bulk.confirm-modal.confirm-button-text")}`,
    showCancelButton: true,
    confirmButtonColor: '#556ee6',
    cancelButtonColor: "rgba(221, 51, 51, 0.7)",
  }).then((result) => {
    if (!result.value) {
      return;
    }

    // Asynchronous stuff requires this method chaining (or many, many promises)
    restService.get(qualificationsUrl)
      .then(qualifications => {
      if (!qualifications) {
        toastrService.showError(i18n.t("talents.create.bulk.no-subscriptions-found"));
        return;
      }
      // Asynchronous stuff requires this method chaining (or many, many promises)
      _uploadTalents(talents, departmentId, pools, qualifications);
    });


  })
}

function _mapHeadersToInternalFormat(talents: TemplateTalentModel[], qualifications: QualificationModel[]): TalentModel[] {
  const mappedTalents = talents.map(t => {
    return {
      firstName: t.Vorname,
      lastName: t.Nachname,
      city: t.Stadt,
      email: t.Email,
      zip: t.PLZ.toString(),
      address: t.Adresse,
      qualificationIds: [t.JobID],
      telephone: t.Telefonnummer
    }
  });

  return mappedTalents;
}

export const bulkImportService = {
  addMultipleTalentsByFile,
}